<template>
  <v-card flat class="pa-6">
    <h2 class="text-h5 primary-header sans-serif">
      {{ $t('form.import.fieldMapper.title') }}
    </h2>
    <v-card-subtitle>
      {{ $t('form.import.fieldMapper.subtitle') }}
    </v-card-subtitle>
    <v-card-text>
      <v-expansion-panels v-model="expandedPanel">
        <v-expansion-panel
          v-for="mapping in activeMappings"
          :key="mapping.sheetName"
        >
          <v-expansion-panel-header>
            <div>
              <strong>{{ mapping.sheetName }}</strong>
              <v-chip small class="ml-2" color="primary" outlined>
                {{ mapping.entityType }}
              </v-chip>
              <v-chip
                small
                class="ml-2"
                :color="hasUnmappedRequired(mapping) ? 'warning' : 'success'"
              >
                {{ getRequiredFieldsStatus(mapping) }}
              </v-chip>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row class="font-weight-bold text-body-2 mb-2">
              <v-col cols="5">{{ $t('form.import.fieldMapper.fileColumn') }}</v-col>
              <v-col cols="7">{{ $t('form.import.fieldMapper.mapsTo') }}</v-col>
            </v-row>
            <v-divider />
            <template v-for="fieldMapping in mapping.fieldMappings">
              <v-row
                :key="fieldMapping.sourceColumn"
                align="center"
                dense
                class="py-2"
              >
                <v-col cols="5">{{ fieldMapping.sourceColumn }}</v-col>
                <v-col cols="7">
                  <v-select
                    v-model="fieldMapping.targetField"
                    :items="getFieldOptions(mapping)"
                    :placeholder="$t('form.import.fieldMapper.selectField')"
                    clearable
                    dense
                    outlined
                    hide-details
                    @change="onTargetFieldChange(mapping, fieldMapping)"
                  />
                </v-col>
              </v-row>
              <v-divider :key="`divider-${fieldMapping.sourceColumn}`" />
            </template>
            <p v-if="hasUnmappedRequired(mapping)" class="mt-4 warning--text">
              {{ $t('form.import.fieldMapper.requiredFieldsMissing') }}: {{ getUnmappedRequiredLabels(mapping).join(', ') }}
            </p>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import {
  BulkImportWizardColumnMapping,
  BulkImportWizardFieldDefinition,
  BulkImportWizardParsedSheet,
  BulkImportWizardSheetMapping
} from '@betterboards/shared/types/Import'
import { getFieldDefinitions, getRequiredFields } from '@betterboards/shared/helpers/import'
import {
  autoMapColumns,
  clearTargetFieldFromOtherMappings,
  cloneBulkImportWizardSheetMappings,
  getUnmappedRequiredFields
} from '@/helpers/import/fieldMapper'

export default Vue.extend({
  name: 'BulkImportFieldMapper',
  props: {
    parsedSheets: {
      type: Array as PropType<BulkImportWizardParsedSheet[]>,
      required: true
    },
    sheetMappings: {
      type: Array as PropType<BulkImportWizardSheetMapping[]>,
      required: true
    }
  },
  data () {
    return {
      localMappings: cloneBulkImportWizardSheetMappings(this.sheetMappings),
      expandedPanel: 0
    }
  },
  computed: {
    activeMappings (): BulkImportWizardSheetMapping[] {
      return this.localMappings.filter(m => m.entityType !== null)
    },
    allRequiredFieldsMapped (): boolean {
      return this.activeMappings.every(m => !this.hasUnmappedRequired(m))
    }
  },
  watch: {
    sheetMappings: {
      handler (newVal: BulkImportWizardSheetMapping[]) {
        this.localMappings = cloneBulkImportWizardSheetMappings(newVal)
        this.initializeFieldMappings()
      },
      deep: true,
      immediate: true
    },
    allRequiredFieldsMapped: {
      handler (isValid: boolean) {
        this.$emit('valid', isValid)
      },
      immediate: true
    }
  },
  methods: {
    initializeFieldMappings (): void {
      let hasChanges = false
      for (const mapping of this.localMappings) {
        if (mapping.entityType === null) {
          continue
        }
        if (mapping.fieldMappings.length === 0) {
          const sheet = this.parsedSheets.find(s => s.name === mapping.sheetName)
          if (sheet) {
            mapping.fieldMappings = autoMapColumns(sheet.columns, mapping.entityType)
            hasChanges = true
          }
        }
      }
      if (hasChanges) {
        this.emitUpdate()
      }
    },
    getFieldOptions (mapping: BulkImportWizardSheetMapping): Array<{ text: string, value: string }> {
      if (!mapping.entityType) {
        return []
      }
      const definitions: BulkImportWizardFieldDefinition[] = getFieldDefinitions(mapping.entityType)
      const sortedDefinitions: BulkImportWizardFieldDefinition[] = [...definitions].sort((a, b) => {
        if (a.required && !b.required) {
          return -1
        }
        if (!a.required && b.required) {
          return 1
        }
        return 0
      })
      return sortedDefinitions.map(d => ({
        text: d.label + (d.required ? ' *' : ''),
        value: d.key
      }))
    },
    onTargetFieldChange (
      mapping: BulkImportWizardSheetMapping,
      currentFieldMapping: BulkImportWizardColumnMapping
    ): void {
      const targetField = currentFieldMapping.targetField
      if (targetField) {
        clearTargetFieldFromOtherMappings(
          mapping.fieldMappings,
          targetField,
          currentFieldMapping.sourceColumn
        )
      }
      this.emitUpdate()
    },
    getRequiredFieldsStatus (mapping: BulkImportWizardSheetMapping): string {
      if (!mapping.entityType) {
        return ''
      }
      const required = getRequiredFields(mapping.entityType)
      const unmapped = getUnmappedRequiredFields(mapping.fieldMappings, mapping.entityType)
      const mappedCount = required.length - unmapped.length
      return `${mappedCount}/${required.length} ${this.$t('form.import.fieldMapper.requiredMapped')}`
    },
    hasUnmappedRequired (mapping: BulkImportWizardSheetMapping): boolean {
      if (!mapping.entityType) {
        return false
      }
      const unmapped = getUnmappedRequiredFields(mapping.fieldMappings, mapping.entityType)
      return unmapped.length > 0
    },
    getUnmappedRequiredLabels (mapping: BulkImportWizardSheetMapping): string[] {
      if (!mapping.entityType) {
        return []
      }
      const unmapped = getUnmappedRequiredFields(mapping.fieldMappings, mapping.entityType)
      return unmapped.map(f => f.label)
    },
    emitUpdate (): void {
      this.$emit('mappingsComplete', cloneBulkImportWizardSheetMappings(this.localMappings))
    }
  }
})
</script>
